import {
  useMutation, UseMutationOptions, UseMutationResult, useQuery, useQueryClient, UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import {
  STAFF_CLAIM_BACKFILL_RUNS_QUERY_KEY,
  StaffClaimBackfillApiResponse,
  StaffClaimBackfillRun,
  StaffClaimBackfillRunsApiResponse,
  StaffClaimBackfillSummary,
} from './types';

type BackfillParams = { dryRun: boolean };

/**
 * Backfill claimu `isStaff` dla całego personelu (v3-backfillStaffClaims).
 *
 * `firestore.rules` rozpoznają personel po tym claimie — konto bez niego traci
 * dostęp do zamówień, list śniadaniowych i zgłoszeń w panelu hotelu. Endpoint
 * chodzi po stronie backendu (Admin SDK), więc nie potrzeba lokalnych poświadczeń.
 * `dryRun: true` niczego nie zapisuje — zwraca listę kont do przejrzenia.
 */
export const useBackfillStaffClaims = (
  options?: UseMutationOptions<StaffClaimBackfillSummary, AxiosError, BackfillParams>,
): UseMutationResult<StaffClaimBackfillSummary, AxiosError, BackfillParams> => {
  const queryClient = useQueryClient();

  return useMutation<StaffClaimBackfillSummary, AxiosError, BackfillParams>({
    mutationFn: async ({ dryRun }) => {
      const res = await axiosInstance.post<StaffClaimBackfillApiResponse>(
        '/v3-backfillStaffClaims',
        { dryRun },
      );
      return res.data.data;
    },
    // Każdy przebieg (także podgląd) ląduje w historii — odświeżamy ją.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STAFF_CLAIM_BACKFILL_RUNS_QUERY_KEY] });
    },
    ...options,
  });
};

/** Historia przebiegów backfillu (kto, kiedy, ile kont) — ostatnie 20. */
export const useGetStaffClaimBackfillRuns = (
  enabled = true,
): UseQueryResult<StaffClaimBackfillRun[], AxiosError> => useQuery<StaffClaimBackfillRun[], AxiosError>({
  queryKey: [STAFF_CLAIM_BACKFILL_RUNS_QUERY_KEY],
  queryFn: async () => {
    const res = await axiosInstance.get<StaffClaimBackfillRunsApiResponse>('/v3-backfillStaffClaims');
    return res.data.data;
  },
  enabled,
});
